/**
 * PublicCrisisStrip - Drop 2 (5 Oct 2026, CMO review C-01, C-02, C-06, A-2(d)).
 *
 * Numbers only. Renders the existing name and number strings from
 * config/crisisLines.js, verbatim, for Triple Zero, Open Arms and Lifeline,
 * plus the existing "Crisis Support" link label to /crisis. No sentence,
 * heading or reassurance copy: Alex (interim clinical authority) signed the
 * placement on 5 Oct 2026 on the basis of "numbers only, using the existing
 * approved names and numbers, no new sentences". Any wording added here is a
 * Material change under ASV-CMG-005 and needs MOC sign-off.
 *
 * Works with no auth context, no router and no widget provider: every link is
 * a plain <a>, so /crisis is a full page load (the public crisis page renders
 * signed out). Order follows crisisLines.js (Triple Zero first, then the
 * ADF-first order), never re-sorted here.
 *
 * Props
 *   compact     smaller single-row variant (under the chat composer)
 *   tone        'light' (dark text, for light surfaces) or 'dark' (white
 *               text, for the ink loading screens)
 *   linkTarget  optional target for the /crisis link ('_top' inside the
 *               widget iframe so the parent window navigates)
 */
import React from 'react';
import { Box } from '@mui/material';

import { CRISIS_LINES } from '../../config/crisisLines';

const STRIP_IDS = ['triple-zero', 'open-arms', 'lifeline'];
export const STRIP_LINES = CRISIS_LINES.filter((l) => STRIP_IDS.includes(l.id));

const CRISIS_LINK_LABEL = 'Crisis Support';

export default function PublicCrisisStrip({ compact = false, tone = 'light', linkTarget }) {
  const dark = tone === 'dark';
  const textColour = dark ? '#ffffff' : '#1e293b';
  const emergencyColour = dark ? '#ffffff' : '#b71c1c';

  const linkSx = {
    color: textColour,
    fontWeight: 600,
    fontSize: compact ? 12 : 14,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: compact ? 32 : 44,
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: `2px solid ${dark ? '#ffffff' : '#5086ed'}`,
      outlineOffset: 2,
    },
  };

  return (
    <Box
      component="nav"
      aria-label={CRISIS_LINK_LABEL}
      data-testid={compact ? 'public-crisis-strip-compact' : 'public-crisis-strip'}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        columnGap: compact ? 1.5 : 2,
        rowGap: 0,
        py: compact ? 0.25 : 1,
        px: 1,
      }}
    >
      {STRIP_LINES.map((line) => (
        <Box
          key={line.id}
          component="a"
          href={line.tel}
          data-testid={`public-crisis-line-${line.id}`}
          aria-label={line.ariaLabel || `Call ${line.name} on ${line.number}`}
          sx={{
            ...linkSx,
            ...(line.tier === 'emergency' && { color: emergencyColour, fontWeight: 700 }),
          }}
        >
          {line.name}: {line.number}
        </Box>
      ))}
      <Box
        component="a"
        href="/crisis"
        target={linkTarget}
        data-testid="public-crisis-link"
        sx={linkSx}
      >
        {CRISIS_LINK_LABEL}
      </Box>
    </Box>
  );
}
